import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, phpFmt } from "../lib/api";
import ProtectedRoute from "../components/ProtectedRoute";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Shield, Check, FileText, Banknote } from "lucide-react";
import { formatApiError } from "../lib/auth";

function Body() {
    const { centerId } = useParams();
    const [mode, setMode] = useState("cash"); // pdc | cash
    const [center, setCenter] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get(`/centers/${centerId}`).then((r) => setCenter(r.data)).catch(() => setCenter(null));
    }, [centerId]);

    const confirm = async () => {
        setSubmitting(true);
        try {
            await api.post("/agreements", { center_id: centerId, payment_mode: mode });
            toast.success("Agreement created. Check your dashboard.");
            setTimeout(() => navigate("/dashboard/client"), 700);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create agreement");
        } finally {
            setSubmitting(false);
        }
    };

    if (!center) return <div className="pt-32 text-center text-zinc-500 text-sm tracking-label uppercase">Loading…</div>;

    const monthly = center.monthly_rate_php || 0;
    const months = mode === "pdc" ? 12 : 11;
    const total = monthly * months;

    return (
        <div className="pt-24 min-h-screen">
            <Toaster richColors position="top-center" />
            <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-10">
                <Link to={`/centers/${centerId}`} className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-950">
                    <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back
                </Link>
                <div className="text-[10px] uppercase tracking-label text-zinc-500 mt-6">Agreement</div>
                <h1 className="font-display text-5xl font-semibold tracking-tighter mt-2">Confirm your address.</h1>
                <p className="mt-3 text-sm text-zinc-600 max-w-2xl">
                    Pick a payment mode. No online payment is required here — FWG Operations will reach out to finalize your agreement and collect checks or cash.
                </p>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-8">
                    <div className="md:col-span-3 space-y-4">
                        <button
                            data-testid="mode-pdc"
                            type="button"
                            onClick={() => setMode("pdc")}
                            className={`w-full text-left border-2 p-5 transition-colors ${mode === "pdc" ? "border-zinc-950 bg-white" : "border-zinc-200 bg-white hover:border-zinc-400"}`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                    <FileText className="h-5 w-5 mt-0.5" strokeWidth={1.5} />
                                    <div>
                                        <div className="font-display text-xl font-semibold">12 Post-Dated Checks</div>
                                        <div className="text-sm text-zinc-600 mt-1">Issue 12 PDCs, one per month — hands-free billing.</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display text-xl font-semibold">{phpFmt(monthly * 12)}</div>
                                    <div className="text-[10px] uppercase tracking-label text-zinc-500">12 months</div>
                                </div>
                            </div>
                        </button>

                        <button
                            data-testid="mode-cash"
                            type="button"
                            onClick={() => setMode("cash")}
                            className={`w-full text-left border-2 p-5 transition-colors ${mode === "cash" ? "border-[#FF6A13] bg-orange-50/40" : "border-zinc-200 bg-white hover:border-zinc-400"}`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex items-start gap-3">
                                    <Banknote className="h-5 w-5 mt-0.5 text-[#FF6A13]" strokeWidth={1.5} />
                                    <div>
                                        <div className="font-display text-xl font-semibold">
                                            Cash — pay 11 months up-front
                                            <span className="ml-2 align-middle text-[10px] uppercase tracking-label bg-[#FF6A13] text-white px-1.5 py-0.5">Save 1 month</span>
                                        </div>
                                        <div className="text-sm text-zinc-600 mt-1">Pay for 11 months, enjoy 12 months of service.</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="font-display text-xl font-semibold">{phpFmt(monthly * 11)}</div>
                                    <div className="text-[10px] uppercase tracking-label text-zinc-500">11 months cash</div>
                                </div>
                            </div>
                        </button>

                        <button
                            data-testid="confirm-agreement-btn"
                            onClick={confirm}
                            disabled={submitting || monthly <= 0}
                            className="w-full bg-zinc-950 text-white py-4 font-medium hover:bg-[#FF6A13] transition-colors disabled:opacity-40"
                        >
                            {submitting ? "Creating agreement…" : "Confirm agreement"}
                        </button>
                        <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                            <Shield className="h-3.5 w-3.5" strokeWidth={1.5} />
                            A draft lease contract will be generated here in a future release.
                        </div>
                    </div>

                    <aside className="md:col-span-2">
                        <div className="border border-zinc-950 bg-white">
                            <div className="bg-zinc-950 text-white p-5">
                                <div className="text-[10px] uppercase tracking-label text-white/60">Your address</div>
                                <div className="font-display text-xl font-semibold mt-1">{center.name}</div>
                                <div className="text-white/70 text-xs mt-0.5">{center.public_area}</div>
                            </div>
                            <div className="p-5 space-y-3 text-sm">
                                <div className="flex justify-between"><span className="text-zinc-500">Monthly rate</span><span className="font-medium">{phpFmt(monthly)}</span></div>
                                <div className="flex justify-between"><span className="text-zinc-500">Payment mode</span><span className="font-medium uppercase">{mode === "pdc" ? "12 PDCs" : "Cash"}</span></div>
                                <div className="flex justify-between"><span className="text-zinc-500">Months to pay</span><span className="font-medium">{months}</span></div>
                                <div className="flex justify-between"><span className="text-zinc-500">Service term</span><span className="font-medium">12 months</span></div>
                                <div className="border-t border-zinc-200 pt-3 flex justify-between font-display text-xl">
                                    <span>Total</span><span>{phpFmt(total)}</span>
                                </div>
                                <ul className="pt-3 space-y-2 text-xs text-zinc-600">
                                    <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[#FF6A13]" strokeWidth={1.5} /> Business-grade address</li>
                                    <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[#FF6A13]" strokeWidth={1.5} /> Mail handling</li>
                                    <li className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-[#FF6A13]" strokeWidth={1.5} /> Meeting-room access</li>
                                </ul>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}

export default function Agreement() {
    return (
        <ProtectedRoute>
            <Body />
        </ProtectedRoute>
    );
}
